import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  doc,
  deleteDoc,
  updateDoc,
  increment,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { formatTimestamp } from "./IncidentMap";
import Linkify from "react-linkify";

const LinkedText = ({ text }) => (
  <Linkify
    componentDecorator={(decoratedHref, decoratedText, key) => (
      <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key}>
        {decoratedText}
      </a>
    )}
  >
    {text}
  </Linkify>
);

const getUpvoteCount = (item) =>
  typeof item?.upvotes === "number" ? item.upvotes : Math.max(item?.votes || 0, 0);

const getDownvoteCount = (item) =>
  typeof item?.downvotes === "number" ? item.downvotes : Math.max(-(item?.votes || 0), 0);

function ConfirmActionModal({ title, message, confirmLabel, confirmTone = "danger", onCancel, onConfirm }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className={`modal inline-action-modal inline-action-modal-${confirmTone}`} onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="modal-body-copy">{message}</p>
        <div className="modal-form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="submit-btn" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionMessageModal({ title, message, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal inline-action-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="modal-body-copy">{message}</p>
        <div className="modal-form-actions">
          <button type="button" className="submit-btn" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function EditIncidentModal({ initialDescription, onCancel, onSave }) {
  const [draftDescription, setDraftDescription] = useState(initialDescription);

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal inline-action-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Incident</h3>
        <label htmlFor="incident-description-edit" className="modal-field-label">
          Update the incident description
        </label>
        <textarea
          id="incident-description-edit"
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          rows="5"
          className="modal-textarea"
        />
        <div className="modal-form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="submit-btn"
            onClick={() => onSave(draftDescription)}
            disabled={!draftDescription.trim()}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function IncidentPopupContent({ incident, user }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [collapsedComments, setCollapsedComments] = useState({});
  const [showDeleteCommentId, setShowDeleteCommentId] = useState(null);
  const [isEditingIncident, setIsEditingIncident] = useState(false);
  const [isDeletingIncident, setIsDeletingIncident] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  useEffect(() => {
    if (!incident?.id) return;

    const commentsRef = collection(db, "incidents", incident.id, "comments");
    const commentsQuery = query(commentsRef, orderBy("timestamp", "asc"));
    const unsubscribe = onSnapshot(
      commentsQuery,
      (snapshot) => {
        setComments(snapshot.docs.map((commentDoc) => ({ id: commentDoc.id, ...commentDoc.data() })));
        setIsLoadingComments(false);
      },
      (error) => {
        console.error("Error fetching comments:", error);
        setIsLoadingComments(false);
      }
    );

    return () => unsubscribe();
  }, [incident?.id]);

  useEffect(() => {
    setCollapsedComments((prev) => {
      const next = { ...prev };
      const activeCommentIds = new Set(comments.map((comment) => comment.id));

      comments.forEach((comment) => {
        if (typeof next[comment.id] === "undefined") {
          next[comment.id] = getDownvoteCount(comment) >= 5;
        }
      });

      Object.keys(next).forEach((commentId) => {
        if (!activeCommentIds.has(commentId)) {
          delete next[commentId];
        }
      });

      return next;
    });
  }, [comments]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!user || !newComment.trim() || !incident?.id) return;

    try {
      await addDoc(collection(db, "incidents", incident.id, "comments"), {
        text: newComment.trim(),
        userId: user.uid,
        userName: user.displayName || "Anonymous",
        timestamp: serverTimestamp(),
        votes: 0,
        upvotes: 0,
        downvotes: 0,
      });
      setNewComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
      setActionMessage({
        title: "Comment Failed",
        message: "Your comment could not be posted right now. Please try again.",
      });
    }
  };

  const handleVoteIncident = async (voteType) => {
    if (!user || !incident?.id) return;

    try {
      const voteRef = doc(db, `incidents/${incident.id}/votes/${user.uid}`);
      const voteSnap = await getDoc(voteRef);
      if (voteSnap.exists()) {
        setActionMessage({
          title: "Vote Already Recorded",
          message: "You have already voted on this incident.",
        });
        return;
      }

      await setDoc(voteRef, { voteType });
      await updateDoc(doc(db, "incidents", incident.id), {
        votes: increment(voteType === "up" ? 1 : -1),
        upvotes: increment(voteType === "up" ? 1 : 0),
        downvotes: increment(voteType === "down" ? 1 : 0),
      });
    } catch (error) {
      console.error("Error voting on incident:", error);
      setActionMessage({
        title: "Vote Failed",
        message: "Your vote on this incident could not be recorded right now. Please try again.",
      });
    }
  };

  const handleVoteComment = async (commentId, voteType) => {
    if (!user || !incident?.id || !commentId) return;

    const commentVoteRef = doc(db, `incidents/${incident.id}/comments/${commentId}/votes/${user.uid}`);
    const commentRef = doc(db, `incidents/${incident.id}/comments/${commentId}`);

    try {
      const voteSnap = await getDoc(commentVoteRef);
      if (voteSnap.exists()) {
        setActionMessage({
          title: "Vote Already Recorded",
          message: "You have already voted on this comment.",
        });
        return;
      }

      await setDoc(commentVoteRef, { voteType });
      await updateDoc(commentRef, {
        votes: increment(voteType === "up" ? 1 : -1),
        upvotes: increment(voteType === "up" ? 1 : 0),
        downvotes: increment(voteType === "down" ? 1 : 0),
      });
    } catch (error) {
      console.error("Error voting on comment:", error);
      setActionMessage({
        title: "Vote Failed",
        message: "Your vote on this comment could not be recorded right now. Please try again.",
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!user || !incident?.id || !commentId) return;

    try {
      await deleteDoc(doc(db, "incidents", incident.id, "comments", commentId));
      setShowDeleteCommentId(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
      setActionMessage({
        title: "Delete Failed",
        message: "The comment could not be deleted right now. Please try again.",
      });
    }
  };

  const handleSaveIncidentEdit = async (newDescription) => {
    const trimmedDescription = newDescription.trim();
    if (!trimmedDescription || trimmedDescription === incident.description) {
      setIsEditingIncident(false);
      return;
    }

    try {
      await updateDoc(doc(db, "incidents", incident.id), {
        description: trimmedDescription,
      });
      setIsEditingIncident(false);
    } catch (error) {
      console.error("Error editing incident:", error);
      setActionMessage({
        title: "Update Failed",
        message: "The incident description could not be updated right now. Please try again.",
      });
    }
  };

  const handleDeleteIncident = async () => {
    try {
      await deleteDoc(doc(db, "incidents", incident.id));
      setIsDeletingIncident(false);
    } catch (error) {
      console.error("Error deleting incident:", error);
      setActionMessage({
        title: "Delete Failed",
        message: "The incident could not be deleted right now. Please try again.",
      });
    }
  };

  const toggleCommentVisibility = (commentId) => {
    setCollapsedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  return (
    <div>
      <p>
        <strong>{incident.userName || "Anonymous"}:</strong> {incident.description}
      </p>
      <p>
        <small>
          Category: {incident.category || "General"} | Severity: {incident.severity || "Medium"} | Status:{" "}
          {((incident.status || "new").charAt(0).toUpperCase() + (incident.status || "new").slice(1))}
        </small>
      </p>
      {incident.imageUrl && (
        <img
          src={incident.imageUrl}
          alt="Incident"
          style={{ width: "100%", maxHeight: "200px", objectFit: "cover", marginBottom: "10px" }}
        />
      )}
      <p>
        <small>Reported at: {formatTimestamp(incident.timestamp)}</small>
      </p>

      <div className="incident-action-row">
        <button className="incident-action-button" onClick={() => handleVoteIncident("up")}>Upvote</button>
        <span className="vote-value vote-up">{getUpvoteCount(incident)}</span>
        <button className="incident-action-button" onClick={() => handleVoteIncident("down")}>Downvote</button>
        <span className="vote-value vote-down">{getDownvoteCount(incident)}</span>
      </div>

      {user && user.uid === incident.userId && (
        <div className="incident-action-row">
          <button
            onClick={() => setIsEditingIncident(true)}
            className="incident-action-button"
          >
            Edit
          </button>
          <button
            onClick={() => setIsDeletingIncident(true)}
            className="incident-action-button"
          >
            Delete
          </button>
          <select
            value={incident.status || "new"}
            onChange={async (e) => {
              try {
                await updateDoc(doc(db, "incidents", incident.id), { status: e.target.value });
              } catch (error) {
                console.error("Failed to update status:", error);
                setActionMessage({
                  title: "Status Update Failed",
                  message: "The incident status could not be updated right now. Please try again.",
                });
              }
            }}
            className="incident-status-select"
          >
            <option value="new">New</option>
            <option value="verified">Verified</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      )}

      <hr className="my-3" />
      <h4>Comments</h4>

      {isLoadingComments ? (
        <p>Loading comments...</p>
      ) : comments.length === 0 ? (
        <p>
          <small>No comments yet.</small>
        </p>
      ) : (
        <div style={{ maxHeight: "150px", overflowY: "auto", marginBottom: "10px" }}>
          {comments.map((comment) => (
            <div key={comment.id} style={{ borderBottom: "1px solid #eee", padding: "5px 0" }}>
              {getDownvoteCount(comment) >= 5 && collapsedComments[comment.id] ? (
                <div className="comment-collapsed">
                  <small>Comment hidden due to downvotes.</small>
                  <button
                    className="incident-action-button"
                    onClick={() => toggleCommentVisibility(comment.id)}
                    title="Show Comment"
                  >
                    Show
                  </button>
                </div>
              ) : (
                <p style={{ margin: "0 0 3px 0" }}>
                  <LinkedText text={comment.text} />
                </p>
              )}
              <small>
                <strong>{comment.userName || "Anonymous"}</strong> - {formatTimestamp(comment.timestamp)}
              </small>
              <div className="incident-action-row">
                <button
                  className="incident-action-button"
                  onClick={() => handleVoteComment(comment.id, "up")}
                  title="Upvote"
                >
                  Upvote
                </button>
                <span className="vote-value vote-up">{getUpvoteCount(comment)}</span>
                <button
                  className="incident-action-button"
                  onClick={() => handleVoteComment(comment.id, "down")}
                  title="Downvote"
                >
                  Downvote
                </button>
                <span className="vote-value vote-down">{getDownvoteCount(comment)}</span>
                {getDownvoteCount(comment) >= 5 && (
                  <button
                    className="incident-action-button"
                    onClick={() => toggleCommentVisibility(comment.id)}
                    title={collapsedComments[comment.id] ? "Show Comment" : "Hide Comment"}
                  >
                    {collapsedComments[comment.id] ? "Show" : "Hide"}
                  </button>
                )}
                {user && user.uid === comment.userId && (
                  <button
                    onClick={() => setShowDeleteCommentId(comment.id)}
                    className="incident-action-button incident-delete-button"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {user ? (
        <form onSubmit={handleAddComment} className="mt-2">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add your comment..."
            rows="2"
            required
            style={{
              width: "100%",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              marginBottom: "5px",
            }}
          />
          <button type="submit" className="px-3 py-1 bg-blue-500 text-white rounded text-sm">
            Post Comment
          </button>
        </form>
      ) : (
        <p>
          <small>Please login to comment.</small>
        </p>
      )}

      {isEditingIncident && (
        <EditIncidentModal
          initialDescription={incident.description}
          onCancel={() => setIsEditingIncident(false)}
          onSave={handleSaveIncidentEdit}
        />
      )}

      {isDeletingIncident && (
        <ConfirmActionModal
          title="Delete Incident"
          message="This will permanently remove the incident from the map. Do you want to continue?"
          confirmLabel="Delete Incident"
          onCancel={() => setIsDeletingIncident(false)}
          onConfirm={handleDeleteIncident}
        />
      )}

      {showDeleteCommentId && (
        <ConfirmActionModal
          title="Delete Comment"
          message="This comment will be removed permanently. Do you want to continue?"
          confirmLabel="Delete Comment"
          onCancel={() => setShowDeleteCommentId(null)}
          onConfirm={() => handleDeleteComment(showDeleteCommentId)}
        />
      )}

      {actionMessage && (
        <ActionMessageModal
          title={actionMessage.title}
          message={actionMessage.message}
          onClose={() => setActionMessage(null)}
        />
      )}
    </div>
  );
}

export default IncidentPopupContent;
